import React, { useState, useEffect } from 'react';
import emailjs from "@emailjs/browser";
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Clock, Send, Building2, Users, Award } from 'lucide-react';
import { getContactPageData, formatContactPageData } from '../api/strapi/contactPageApi';
import SectionGuard from '../components/SectionGuard';
import PageHero from '../components/common/PageHero';
import SectionHeader from '../components/common/SectionHeader';
import { useLanguage } from '../context';
import { getLocalizedPath } from '../helpers/routes';
import DataLoader from '../components/loader/DataLoader';
import { onToastError, onToastSuccess } from '../helpers/utils';

const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || "service_a9nbxpp";
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || "template_gswbx0a";
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || "KcetoODnwgCfWg-6G";
const CONTACT_EMAIL = process.env.REACT_APP_CONTACT_EMAIL || "contact@africasystems.com";

const ContactPage = () => {
  const intl = useIntl();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    company: '',
    email: '',
    phone: '',
    country: '',
    countryOther: '',
    industry: '',
    industryOther: '',
    description: '',
    optedIn: true
  });
  const [contactData, setContactData] = useState(null);
  const [contactResult, setContactResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mapError, setMapError] = useState(false);
  const { language } = useLanguage();

  useEffect(() => {
    const fetchContactData = async () => {
      try {
        setLoading(true);
        const result = await getContactPageData(language);
        setContactResult(result);

        if (!result.ok) {
          if (result.reason !== "NOT_FOUND") {
            console.warn("Contact page data unavailable:", result);
          }
          setContactData(null);
          return;
        }

        console.log('=== RAW API Response ===', JSON.stringify(result.data, null, 2));
        const formattedData = formatContactPageData(result.data);
        console.log('=== Formatted Data ===', JSON.stringify(formattedData, null, 2));
        console.log('=== location object ===', formattedData?.location);
        console.log('=== location.location ===', formattedData?.location?.location);
        console.log('=== lat/lng ===', formattedData?.location?.location?.lat, formattedData?.location?.location?.lng);
        setContactData(formattedData);
      } catch (error) {
        console.error('Error fetching contact page data:', error);
        setContactData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchContactData();
  }, [language]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
};

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const fullName = `${formData.firstName} ${formData.lastName}`.trim();
    const templateParams = {
      to_email: CONTACT_EMAIL,
      from_name: fullName,
      name: fullName,
      email: formData.email,
      phone: formData.phone,
      company: formData.company,
      country: formData.country === "Other" ? formData.countryOther : formData.country,
      industry: formData.industry === "Other" ? formData.industryOther : formData.industry,
      subject: `Contact request from ${formData.company || fullName}`,
      category: formData.industry,
      message: formData.description,
      type: "company",
      time: new Date().toLocaleString(),
    };

    try {
      const response = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        { publicKey: EMAILJS_PUBLIC_KEY }
      );
      console.log("Email sent:", response.status, response.text);

      setIsSubmitting(false);
      onToastSuccess(intl.formatMessage({ id: "contact.form.success" }));
      setFormData({
        firstName: '',
        lastName: '',
        company: '',
        email: '',
        phone: '',
        country: '',
        countryOther: '',
        industry: '',
        industryOther: '',
        description: '',
        optedIn: true
      });
    } catch (error) {
      console.error("Failed to send email:", error);
      setIsSubmitting(false);
      onToastError(intl.formatMessage({ id: "internal.problem" }));
    }
  };

  // Map icon names to actual icon components
  const getIconComponent = (iconName) => {
    switch(iconName) {
      case 'Email':
        return <Mail className="w-6 h-6" />;
      case 'Phone':
        return <Phone className="w-6 h-6" />;
      case 'MapIn':
        return <MapPin className="w-6 h-6" />;
      case 'Clock':
        return <Clock className="w-6 h-6" />;
      default:
        return <Mail className="w-6 h-6" />;
    }
  };

  const contactInfo = contactData?.contactInfo?.map(info => ({
    ...info,
    icon: getIconComponent(info.icon)
  })) || [];

  const stats = contactData?.contactStats?.map(stat => ({
    ...stat,
    icon: stat.label.includes('Client') ? Users :
          stat.label.includes('Year') ? Award :
          stat.label.includes('Support') ? Clock : Building2
  })) || [];

  const mapLat = Number(contactData?.location?.location?.lat);
  const mapLng = Number(contactData?.location?.location?.lng);
  const hasMapCoords = Number.isFinite(mapLat) && Number.isFinite(mapLng);
  
  // Google Maps Embed URL (simple version without API key)
  const mapEmbedUrl = hasMapCoords
    ? `https://www.google.com/maps?q=${mapLat},${mapLng}&hl=fr&z=15&output=embed`
    : null;

  useEffect(() => {
    setMapError(false);
    console.log('=== mapLat:', mapLat, 'mapLng:', mapLng);
    console.log('=== hasMapCoords:', hasMapCoords);
    console.log('=== mapEmbedUrl:', mapEmbedUrl);
  }, [mapLat, mapLng, hasMapCoords, mapEmbedUrl]);

  if (loading) {
    return <DataLoader isLoading={true} />;
  }

  const sectionFallback = (
    <div className="py-10 text-center text-gray-500 text-sm">
      {intl.formatMessage({ id: "empty.section" })}
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{contactData?.title ? `${contactData.title} | Africa Systems` : intl.formatMessage({ id: "contact.meta.title" })}</title>
        <meta name="description" content={intl.formatMessage({ id: "contact.meta.description" })} />
      </Helmet>

      {/* Hero Section */}
      <SectionGuard result={contactResult} fallback={sectionFallback} logLabel="Contact hero">
        <PageHero
          eyebrow="Africa Systems"
          title={contactData?.heroComponent?.title}
          description={contactData?.heroComponent?.description}
          image={contactData?.heroComponent?.image}
        />
      </SectionGuard>

      {/* Stats Section */}
      <SectionGuard result={contactResult} fallback={null} logLabel="Contact stats">
        {contactData?.contactStats && contactData.contactStats.length > 0 && (
          <section className="py-12">
            <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                {stats.map((stat, index) => (
                  <motion.div
                    key={stat.id || index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="text-center"
                  >
                    <div className="text-3xl font-bold text-[#0a1628] mb-2">{stat.value}</div>
                    <div className="text-gray-600">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}
      </SectionGuard>

      {/* Contact Form and Info */}
      <section className="py-16">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-white border border-slate-200 hover:border-[#16A34A]/40 hover:shadow-lg transition-all duration-300 rounded-xl p-8"
            >
              <h2 className="text-3xl font-bold text-[#0a1628] mb-6">{intl.formatMessage({ id: "contact.form.title" })}</h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {intl.formatMessage({ id: "contact.form.firstName" })} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg focus:border-[#16A34A] focus:ring-2 focus:ring-[#16A34A]/30 outline-none transition-colors"
                      placeholder={intl.formatMessage({ id: "contact.form.firstNamePlaceholder" })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {intl.formatMessage({ id: "contact.form.lastName" })} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg focus:border-[#16A34A] focus:ring-2 focus:ring-[#16A34A]/30 outline-none transition-colors"
                      placeholder={intl.formatMessage({ id: "contact.form.lastNamePlaceholder" })}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {intl.formatMessage({ id: "contact.form.company" })} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg focus:border-[#16A34A] focus:ring-2 focus:ring-[#16A34A]/30 outline-none transition-colors"
                    placeholder={intl.formatMessage({ id: "contact.form.companyPlaceholder" })}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {intl.formatMessage({ id: "contact.form.email" })} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg focus:border-[#16A34A] focus:ring-2 focus:ring-[#16A34A]/30 outline-none transition-colors"
                      placeholder={intl.formatMessage({ id: "contact.form.emailPlaceholder" })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {intl.formatMessage({ id: "contact.form.phone" })}
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg focus:border-[#16A34A] focus:ring-2 focus:ring-[#16A34A]/30 outline-none transition-colors"
                      placeholder={intl.formatMessage({ id: "contact.form.phonePlaceholder" })}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {intl.formatMessage({ id: "contact.form.country" })}
                    </label>
                    <select
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg focus:border-[#16A34A] focus:ring-2 focus:ring-[#16A34A]/30 outline-none transition-colors"
                    >
                      <option value="">{intl.formatMessage({ id: "contact.form.countryPlaceholder" })}</option>
                      <option value="Cameroon">{intl.formatMessage({ id: "country.cameroon" })}</option>
                      <option value="Nigeria">{intl.formatMessage({ id: "country.nigeria" })}</option>
                      <option value="Ghana">{intl.formatMessage({ id: "country.ghana" })}</option>
                      <option value="Kenya">{intl.formatMessage({ id: "country.kenya" })}</option>
                      <option value="South Africa">{intl.formatMessage({ id: "country.southAfrica" })}</option>
                      <option value="Other">{intl.formatMessage({ id: "common.other" })}</option>
                    </select>
                    {formData.country === "Other" && (
                      <input
                        type="text"
                        name="countryOther"
                        value={formData.countryOther}
                        onChange={handleChange}
                        className="mt-3 w-full px-4 py-3 bg-white border border-slate-300 rounded-lg focus:border-[#16A34A] focus:ring-2 focus:ring-[#16A34A]/30 outline-none transition-colors"
                        placeholder={intl.formatMessage({ id: "contact.form.countryOtherPlaceholder" })}
                      />
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {intl.formatMessage({ id: "contact.form.industry" })}
                    </label>
                    <select
                      name="industry"
                      value={formData.industry}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg focus:border-[#16A34A] focus:ring-2 focus:ring-[#16A34A]/30 outline-none transition-colors"
                    >
                      <option value="">{intl.formatMessage({ id: "contact.form.industryPlaceholder" })}</option>
                      <option value="Telecom">{intl.formatMessage({ id: "industry.telecom" })}</option>
                      <option value="Security">{intl.formatMessage({ id: "industry.security" })}</option>
                      <option value="Manufacturing">{intl.formatMessage({ id: "industry.manufacturing" })}</option>
                      <option value="Government">{intl.formatMessage({ id: "industry.government" })}</option>
                      <option value="Transportation">{intl.formatMessage({ id: "industry.transportation" })}</option>
                      <option value="Other">{intl.formatMessage({ id: "common.other" })}</option>
                    </select>
                    {formData.industry === "Other" && (
                      <input
                        type="text"
                        name="industryOther"
                        value={formData.industryOther}
                        onChange={handleChange}
                        className="mt-3 w-full px-4 py-3 bg-white border border-slate-300 rounded-lg focus:border-[#16A34A] focus:ring-2 focus:ring-[#16A34A]/30 outline-none transition-colors"
                        placeholder={intl.formatMessage({ id: "contact.form.industryOtherPlaceholder" })}
                      />
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {intl.formatMessage({ id: "contact.form.needs" })} <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg focus:border-[#16A34A] focus:ring-2 focus:ring-[#16A34A]/30 outline-none transition-colors"
                    placeholder={intl.formatMessage({ id: "contact.form.needsPlaceholder" })}
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="optedIn"
                    checked={formData.optedIn}
                    onChange={handleChange}
                    id="optedIn"
                    className="h-4 w-4 text-[#16A34A] focus:ring-[#16A34A] border-slate-300 rounded"
                  />
                  <label htmlFor="optedIn" className="ml-2 block text-sm text-gray-700">
                    {intl.formatMessage({ id: "contact.form.optIn" })}
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#16A34A] hover:bg-[#15803D] text-white px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.14em] transition-colors rounded-lg flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      {intl.formatMessage({ id: "contact.form.sending" })}
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      {intl.formatMessage({ id: "contact.form.send" })}
                    </>
                  )}
                </button>
              </form>
            </motion.div>

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div>
                <SectionHeader
                  eyebrow="Africa Systems"
                  title={intl.formatMessage({ id: "contact.info" })}
                  description={intl.formatMessage({ id: "contact.info.description" })}
                />
              </div>

              <div className="space-y-6">
                {contactInfo.map((item, index) => (
                  <motion.div
                    key={item.id || index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-start p-6 bg-white border border-slate-200 hover:border-[#16A34A]/40 hover:shadow-lg transition-all duration-300 rounded-xl"
                  >
                    <div className="flex-shrink-0 bg-[#16A34A]/10 p-3 rounded-full text-[#16A34A]">
                      {item.icon}
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-bold text-[#0a1628]">{item.title}</h3>
                      <p className="text-gray-600 font-medium">{item.content}</p>
                      <p className="text-sm text-gray-500">{item.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Office Location */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
                className="bg-white border border-slate-200 hover:border-[#16A34A]/40 hover:shadow-lg transition-all duration-300 rounded-xl overflow-hidden"
              >
                {hasMapCoords && !mapError ? (
                  <div className="h-64 relative">
                    <iframe
                      src={mapEmbedUrl}
                      title={intl.formatMessage({ id: "contact.office.mapAlt" })}
                      className="w-full h-full border-0"
                      loading="lazy"
                      referrerPolicy="no-referrer-policy"
                      allowFullScreen
                    />
                  </div>
                ) : (
                  <div className="h-64 bg-slate-200 flex items-center justify-center">
                    <div className="text-center">
                      <MapPin className="w-12 h-12 text-[#16A34A] mx-auto mb-2" />
                      <p className="text-gray-600">{intl.formatMessage({ id: "contact.office.mapLabel" })}</p>
                    </div>
                  </div>
                )}
                <div className="p-6">
                  <h3 className="text-lg font-bold text-[#0a1628] mb-2">{intl.formatMessage({ id: "contact.office.title" })}</h3>
                  <p className="text-gray-600">{contactData?.location?.location?.name || intl.formatMessage({ id: "contact.office.fallback" })}</p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Partner Section */}
      <SectionGuard result={contactResult} fallback={null} logLabel="Contact partner section">
        {contactData?.partnerSection && (
          <section className="py-20 bg-[#0a1628]">
            <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                  {contactData.partnerSection.title}
                </h2>
                <p className="text-xl text-white/75 max-w-2xl mx-auto mb-8">
                  {contactData.partnerSection.description}
                </p>
                <a
                  href={getLocalizedPath("/partners", language)}
                  className="inline-block bg-white text-[#16A34A] hover:bg-slate-100 font-semibold py-3 px-8 rounded-lg transition-colors duration-200"
                >
                  {intl.formatMessage({ id: "contact.partner.cta" })}
                </a>
              </motion.div>
            </div>
          </section>
        )}
      </SectionGuard>
    </div>
  );
};

export default ContactPage;